import { isNil } from 'lodash';
import { MouseEvent, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import {
  SingleComponentForm,
  SingleComponentFormProps,
} from '@shared/scenes/cards/singleComponentForms/SingleComponentForm';
import { useCollectionViewInputButtonTarget } from '@shared/scenes/entity/util/useInputButtonTarget';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { CardCollectionViewFieldContainer } from '../../fields/CardCollectionViewFieldContainer';
import { WorkflowEntityProvider } from './provider';
import { useField, useLoadingAndError, useUpdateField } from './workflowEntityHooks';

interface InputButtonProps {
  component: API.InputButtonComponent;
  workflowTemplateId: string;
  entityId: string;
  analytics?: CollectionViewAnalytics;
  canUpdateCard?: boolean;
  /** Renders `data-with-label` on the container so per-view CSS can add label spacing. */
  withLabel?: boolean;
}

export function InputButton(props: InputButtonProps) {
  if (!props.canUpdateCard) {
    return null;
  }

  return <InputButtonContent {...props} />;
}

function InputButtonContent({
  component,
  workflowTemplateId,
  entityId,
  analytics,
  withLabel,
}: InputButtonProps) {
  const anchorName = getAnchorName('input-button', entityId, component.inputComponentId);
  const [showForm, setShowForm] = useState(false);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const target = useCollectionViewInputButtonTarget(workflowTemplate, component.inputComponentId);
  const readOnlyTooltip = i18n('card.form.inputButton.readOnlyTooltip', { label: target.label });
  const tooltip = target.isReadOnly && readOnlyTooltip;

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    analytics?.trackButtonClick(entityId);
    setShowForm(prev => !prev);
  };

  return (
    <CardCollectionViewFieldContainer fieldType="InputButton" withLabel={withLabel}>
      {/* On the View because the button is disabled/unhoverable whenever the tooltip shows. */}
      <View row tooltip={tooltip}>
        <Button
          variant="secondary"
          label={component.buttonText}
          numberOfLines={1}
          onClick={onClick}
          style={{ anchorName }}
          size="medium"
          disabled={target.isReadOnly}
        />
      </View>
      {showForm && !target.isReadOnly && !isNil(workflowTemplate) && (
        <WorkflowEntityProvider entityId={entityId}>
          <InputButtonForm
            fieldName={component.inputComponentId}
            anchorName={anchorName}
            onClose={() => setShowForm(false)}
            workflowTemplate={workflowTemplate}
          />
        </WorkflowEntityProvider>
      )}
    </CardCollectionViewFieldContainer>
  );
}

type InputButtonFormProps = Omit<SingleComponentFormProps, 'field' | 'loading' | 'onUpdateField'>;

function InputButtonForm({
  fieldName,
  anchorName,
  workflowTemplate,
  onClose,
}: InputButtonFormProps) {
  const { loading } = useLoadingAndError();
  const field = useField(fieldName);
  const onUpdateField = useUpdateField();

  return (
    <SingleComponentForm
      fieldName={fieldName}
      field={field}
      anchorName={anchorName}
      workflowTemplate={workflowTemplate}
      onClose={onClose}
      loading={loading}
      onUpdateField={onUpdateField}
    />
  );
}
